import { IDocumentService, IWindowService } from "angular";
import { BehaviorSubject, Subject } from "rxjs";
import type { CompanyDTO } from "src/AngularJS/Admin/Company/company.model";
import { CXOneTokenUrls, CXOneAnalyticsValues } from "src/AngularJS/Navigation/navigation.model";
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { ModuleInfo } from "src/shared/models/module-info.model";
import { Dashboard } from "../models/dashboard.model";
import { ClearViewUtils } from "../utils";
import { CvApiServiceBase } from "./cv-api-service-base";
import { CvConfigurationServiceBase } from "./cv-configuration-service-base";
import { CvLocalizationServiceBase } from "./cv-localization-service-base";
import { IModalService } from "angular-ui-bootstrap";
import { FeedbackDTO } from "src/app/navigation/navigation-header/submit-feedback/submit-feedback.component";
import { ApiResponse } from "../models/api-response.model";

export type CompanyChangedArgs = {
    company?: CompanyDTO;
    accessToken?: string;
};

export type ModuleGroup = {
    icon: string;
    modules: ModuleInfo[];
    localizedGroup: string;
};
export class NavigationServiceBase {
    hasInitializedWithToken = false;
    hasNavigationData = false;
    hasResponsiveDashboardAccess = false;
    sidebarActiveSubject = new BehaviorSubject<boolean>(false);
    get sidebarActive() { return this.sidebarActiveSubject.getValue(); }
    set sidebarActive(isActive: boolean) { this.sidebarActiveSubject.next(isActive); }
    sidebarActiveState = false;
    headerSecurity = {};
    isLoggedIn = false;
    loggedInID = 0;
    loggedInName = '';
    loggedInEmail = '';
    loggedInImageSource = '';
    profilePictureApproved = true;
    loggedInTitle = '';
    loggedInLang = '';
    isImpersonatedUser = false;
    isAdminUser = false;
    companyName = '';
    companyPartner = 0;
    companyVersion = '';
    workspaceName = '';
    widgetsName = '';
    displayModules = '';
    modules: ModuleInfo[] = []; // List of modules sorted by LocalizedName
    groupedModules = new BehaviorSubject<ModuleGroup[]>([]); // List of module group objects sorted by LocalizedGroup value
    isResponsiveDashboard = false;
    shouldGroupModules = true;
    modulesLoading = false;
    activeDashboard: Dashboard = null;
    companyId = null;
    pageGroups = [];
    showWorkspace = false;
    workspaceUrl = '';
    selectedPageGroup = false;
    addingModule = false;
    applyingFilters = false;
    feedbackPlaceHolder = "Type reason here";
    hasUnicode = false;
    confirmationPopup = {
        showPopup: false,
        type: ""
    };

    cxoneTokenUrls: CXOneTokenUrls = undefined;
    cxoneAnalyticsValues: CXOneAnalyticsValues = undefined;

    hasPartnerPMAccess = false;

    recipientUUID = '';

    showOverlaySubject = new BehaviorSubject<boolean>(false);
    get showOverlay() { return this.showOverlaySubject.value; }
    set showOverlay(val: boolean) { this.showOverlaySubject.next(val); }

    companyChanged: Subject<CompanyChangedArgs> = new Subject<CompanyChangedArgs>();
    closeModuleMenuSubject: Subject<void> = new Subject<void>();
    protected dataObj: NavigationServiceBase = this; // for old JS stuff
    $uibModal: IModalService; // To open UserProfile modal from AngularJS

    // Module groups and their icons (the keys are the unlocalized values from the DashboardModulesResponsive Table)
    // This is the order they should display:
    private moduleGroups = {
        "Metric Visualization": "performance",
        CXone_ACD: "performance", // From DashboardModuleNameOverrides table (replaces Metric Visualization for CXone)
        CXone_QM: "report",
        CXone_IA: "tools",
        CXone_Coaching: "coaching",
        CXone_Metrics: "metrics",
        CXone_PM: "gamification",
        CXone_WFM: "workforce-management",
        "Real-Time": "real-time",
        Reports: "report",
        Gamification: "gamification",
        Tools: "tools",
        "Free Time": "free-time",
        Custom: "company", // This is the catch-all group
    };

    private tabId: string = "";

    private fedRAMPBC;

    private fedRAMPTimeout;

    constructor(
        fromSharedService: boolean = false,
        private config?: CvConfigurationService,
        private $window?: Window,
        private cvApiService?: CvApiServiceBase,
        private cvLocalizationService?: CvLocalizationServiceBase,
    ) {
        if (fromSharedService)
            return NavigationServiceBase.getServiceBase();

        if (!cvApiService)
            this.cvApiService = CvApiServiceBase.getServiceBase();

        if (!cvLocalizationService)
            this.cvLocalizationService = CvLocalizationServiceBase.getServiceBase();

        if (!$window)
            this.$window = $window = window;

        if (!config) {
            this.config = CvConfigurationServiceBase.getServiceBase();
        }

        if ($window['navigationData'] !== undefined) {
            const navData =  { ...$window['navigationData'] };
            $window['navigationData'] = null;
            const scriptTag = document.querySelector('script.navigationData');
            scriptTag.parentNode.removeChild(scriptTag);
            this.hasNavigationData = true;

            this.headerSecurity = navData.HeaderSecurity;
            this.loggedInID = navData.LoggedInID;
            this.loggedInName = navData.LoggedInName;
            this.loggedInEmail = navData.LoggedInEmail;
            this.loggedInTitle = navData.LoggedInTitle;
            this.loggedInLang = navData.LoggedInLang;
            this.isImpersonatedUser = navData.IsImpersonatedUser;
            this.isAdminUser = navData.isAdminUser;
            this.companyName = navData.CompanyName;
            this.companyId = navData.CompanyID;
            this.companyPartner = navData.CompanyPartner;
            this.companyVersion = navData.CompanyVersion || $window['_Version'];
            this.pageGroups = navData.PageGroups;
            this.sidebarActive = navData.SidebarActive;
            this.sidebarActiveState = navData.SidebarActive;
            this.shouldGroupModules = navData.ShouldGroupModules;
            this.workspaceUrl = navData.HomePath;
            this.showWorkspace = navData.HomePath != null;
            this.workspaceName = navData.WorkspaceName;
            this.widgetsName = navData.WidgetsName;
            this.cxoneTokenUrls = navData.CXOneTokenUrls;
            this.cxoneAnalyticsValues = navData.CXOneAnalyticsValues;
            this.hasUnicode = navData.HasUnicode;

            this.hasPartnerPMAccess = navData.HasPartnerPMAccess;

            this.recipientUUID = navData.recipientUUID;

            if (navData.LoggedInName != null)
                this.isLoggedIn = true;

            if (navData.CompanyFedRAMP)
                this.setupFedRAMP();
        }

        this.companyChanged.subscribe(({ company, accessToken }) => {
            this.tearDownFedRAMP();

            if (company != null) {
                this.companyId = company.CompanyID;
                this.companyName = company.CompanyName;
                this.initializeWithToken();

                if(company.FedRAMP)
                    this.setupFedRAMP();
            } else {
                this.companyId = null;
                this.companyName = '';
            }

            this.cvApiService.apiToken = accessToken;
        });

        this.isResponsiveDashboard = document.getElementById('webParts') == null; // using as a marker to distinguish between responsive and legacy dashboards

        if (this.cvApiService.apiToken) {
            this.initializeWithToken();
        }
    }

    static getServiceBase(): NavigationServiceBase {
        return ClearViewUtils.getSharedService(NavigationServiceBase, 'NavigationServiceBase');
    }

    public setUibModalService($uibModal: IModalService) {
        this.$uibModal = $uibModal;
    }

    initializeWithToken() {
        if (this.hasInitializedWithToken)
            return;

        this.loadLocalization();
        this.loadProfileImage();
        this.hasInitializedWithToken = true;
    }

    async logout() {
        await this.cvApiService.get('/api/v1/logout');
        this.$window.location.href = `${_BaseURI}/Login.aspx`;
    }

    async updateLanguage(language: string) {
        await this.cvApiService.post(`/api/v1/updatelanguage/${language}`);
        this.$window.location.reload();
    }

    async setSidebarState(state: boolean) {
        await this.cvApiService.post(`/api/v1/sidebarActive/${state}`);
    }

    async endImpersonateUser() {
        return this.cvApiService.get('/api/v1/EndImpersonateUser/');
    }

    async loadModules() {
        if (this.displayModules === 'legacy') {
            return this.cvApiService.get('/api/v2/legacyModules');
        }

        // load responsive if not legacy
        return this.cvApiService.get('/api/v2/modules');
    }

    async submitFeedback(rating: number, body: string){
        const feedback = new FeedbackDTO();
        feedback.Rating = rating;
        feedback.Body = body;
        feedback.URL = window.location.href;
        try {
            const url = '/api/v1/feedback';
            const response = await this.cvApiService.post(url, JSON.stringify(feedback)) as ApiResponse<void>;
            if (response.status_code >= 200 && response.status_code < 300) {
                return response;
            }
            throw response;
        } catch (err) {
            console.error("Failed submitting feedback");
            throw err;
        }
      }

    /**
     * This function sets the dataObj.modules and dataObj.groupedModules object fields to the modules from moduleList
     *
     * @param moduleList List of DashboardModuleResponsive objects
     */
    setModules(moduleList: ModuleInfo[]) {
        if (!this.isResponsiveDashboard) {
            this.modules = moduleList?.slice();
            return;
        }

        const ungroupedModules: ModuleInfo[] = [];
        const groupedModules: { [group: string]: ModuleGroup } = {};
        moduleList?.forEach((m) => {
            ungroupedModules.push(m);
            const moduleGroup = m.ModuleGroup in this.moduleGroups ? m.ModuleGroup : "Custom";
            if (moduleGroup in groupedModules) {
                groupedModules[moduleGroup].modules.push(m);
            } else {
                groupedModules[moduleGroup] = {
                    icon: this.moduleGroups[moduleGroup],
                    modules: [m],
                    localizedGroup: m.LocalizedGroup
                };
            }
        });

        ungroupedModules.sort(this.moduleNameComparer);
        this.modules = ungroupedModules;

        // Rename the Custom group to the company's name:
        if ("Custom" in groupedModules) {
            groupedModules.Custom.localizedGroup = this.companyName;

            if (this.companyName == "Concentrix") {
                groupedModules.Custom.icon = "company-concentrix";
            }
        }

        // Push onto the dataObj.groupedModules array:
        const sortedGroupedModules = [];
        for (const groupKey in this.moduleGroups) { // Assuming IE-11 iterates over the same order moduleGroups is defined...
            if (groupKey in groupedModules) {
                if (groupKey === "CXone_ACD" && this.config.isCxOne) {
                    //cxonePosition is set in a switch statement in responsive-dashboard.component.ts and can be modified for custom orderings
                    groupedModules[groupKey].modules.sort((a, b) => a.cxonePosition - b.cxonePosition);
                    sortedGroupedModules.push(groupedModules[groupKey]);
                    continue;
                }
                // Sort the modules inside the group alphabetically before insertion
                groupedModules[groupKey].modules.sort(this.moduleNameComparer);
                sortedGroupedModules.push(groupedModules[groupKey]);
            }
        }
        this.groupedModules.next(sortedGroupedModules);
    };

    setGroupModuleState(shouldGroup: boolean) {
        return this.cvApiService.post("/api/v2/navigation/setGroupModules", shouldGroup);
    };

    protected async loadLocalization() {
        const submitFeedbackPopupLocalization = await this.cvLocalizationService.GetSection("Submit_Feedback_Popup");
        if (submitFeedbackPopupLocalization?.has("Reason_Placeholder")) {
            this.feedbackPlaceHolder = submitFeedbackPopupLocalization.get("Reason_Placeholder");
        }
    }

    protected async loadProfileImage() {
        const response = await this.cvApiService.get<any>('/api/v1/profile/imagePath');
        this.loggedInImageSource = response.data?.ImageSource;
        this.profilePictureApproved = response.data?.IsApproved;
    }

    /** A comparer function used to help sort lists of modules by LocalizedName (or ModuleName, if LocalizedName is falsy) */
    private moduleNameComparer(a: ModuleInfo, b: ModuleInfo) {
        if (a === b && a == null)
            return 0;
        if (a != null && b == null)
            return 1;
        if (a == null && b != null)
            return -1;
        // both not null:
        const aName = a.LocalizedName || a.ModuleName || "";
        const bName = b.LocalizedName || b.ModuleName || "";
        return aName.localeCompare(bName, undefined, { sensitivity: "base" });
    }

    private setupFedRAMP() {
        // Create a tabId
        for (let i = 0; i < 19; ++i) {
            this.tabId += Math.floor(256 * Math.random()).toString(16).padStart(2, "0");
        }

        this.fedRAMPBC = new BroadcastChannel("FedRAMP");
        let events = ['mousedown', 'keypress', 'scroll', 'touchstart',];
        events.forEach(e => {
            window.addEventListener(e, this.setFedRAMPTimeout.bind(this));
        });
        this.fedRAMPBC.onmessage = this.onFedRAMPMessage.bind(this);
        this.setFedRAMPTimeout();
    }

    private tearDownFedRAMP() {
        if (this.fedRAMPTimeout) {
            clearTimeout(this.fedRAMPTimeout);
        }
        this.fedRAMPBC = null;
    }

    private setFedRAMPTimeout() {
        // Take control and clear other tab's timeouts
        this.fedRAMPBC.postMessage(`${this.tabId},0`);
        // If this tab has an active timeout, clear it.
        if (!!this.fedRAMPTimeout)
            clearTimeout(this.fedRAMPTimeout);
        // Set a new timeout
        this.fedRAMPTimeout = setTimeout(this.handleFedRAMPTimeout.bind(this), 900000);
    }

    private async handleFedRAMPTimeout() {
        // Logout and notify other tabs to reload
        window["__zone_symbol__beforeunloadfalse"] = [];
        await this.logout();
        this.fedRAMPBC.postMessage(`${this.tabId},1`);
    }

    private onFedRAMPMessage(ev) {
        if (ev?.data?.length == 0)
            return;
        let data = ev.data.split(",");
        // Ignore our own messages
        if (data[0] == this.tabId)
            return;

        switch (data[1]) {
            case "0": // Another tab is active so clear this tabs timeout
                clearTimeout(this.fedRAMPTimeout);
                break;
            case "1": // Another tab has triggered logout so reload
                location.reload();
                break;
        }
    }
}
